import React from "react";
import { useAppSettingsService } from "src/services/appSettingsService/appSettingsService";
import { useSelector } from "src/store";
import { makeStyles } from "tss-react/mui";

import { selectGroups } from "@dashboard/groups/store/selectors";

import { EventChartCard } from "../EventChartCard/EventChartCard.lazy";

import EnrollmentProgressCard from "./EnrollmentProgressCard/EnrollmentProgressCard";
import { ManagedAppsCard } from "./ManagedAppsCard/ManagedAppsCard";
import OnlineStatusCard from "./OnlineStatusCard/OnlineStatusCard";
import { OperatingSystemsCard } from "./OperatingSystemsCard/OperatingSystemsCard";
import { ProductsCard } from "./ProductsCard/ProductsCard";
import SubscriptionStatusCard from "./SubscriptionStatusCard/SubscriptionStatusCard";
import { WarningCard } from "./WarningCard";

// The statistic cards switch between a compact and a wide arrangement at one shared breakpoint: below it the four
// status cards sit two per row and each table card takes a full row; from it on all four status cards share one row
// and the three table cards another. The breakpoint is measured against the width this section gets, not against the
// viewport, because the open sidebar takes a different share of the viewport depending on its state.
//
// "xl" (1536px) is the smallest theme breakpoint at which the table cards fit side by side. There, each card gets
// about 500px, and the widest table, Managed applications, needs about 435px to show its columns without scrolling
// sideways (measured in the staging workspace; most of it is the longest application name). At "lg" (1200px) each
// card would get only about 390px.

const useStyles = makeStyles()((theme) => ({
    section: {
        containerType: "inline-size",
        display: "grid",
        gap: theme.spacing(2),
        gridTemplateColumns: "minmax(0, 1fr)",
    },
    statusCards: {
        display: "grid",
        gap: theme.spacing(2),
        gridTemplateColumns: "minmax(0, 1fr)",
        [theme.containerQueries.up("sm")]: {
            gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
        },
        [theme.containerQueries.up("xl")]: {
            gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
        },
    },
    tableCards: {
        display: "grid",
        gap: theme.spacing(2),
        gridTemplateColumns: "minmax(0, 1fr)",
        [theme.containerQueries.up("xl")]: {
            gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
        },
    },
}));

export const StatisticSection = (props: { filter: number[] }) => {
    const { classes } = useStyles();
    const { effectiveWorkspaceSettings } = useAppSettingsService();
    const activityChartEnabled = effectiveWorkspaceSettings.isOverviewPageShowingActivityChart;
    const groups = useSelector(selectGroups);
    const groupsIds = React.useMemo(() => groups.map((group) => group.id), [groups]);
    const groupsForStatistics = React.useMemo(() => (props.filter.length ? props.filter : groupsIds), [props.filter, groupsIds]);

    return (
        <div className={classes.section}>
            <div className={classes.statusCards}>
                <SubscriptionStatusCard />
                <EnrollmentProgressCard />
                <OnlineStatusCard groupIds={groupsForStatistics} />
                <WarningCard groupIds={groupsForStatistics} />
            </div>
            {activityChartEnabled && <EventChartCard groupIds={groupsForStatistics} />}
            <div className={classes.tableCards}>
                <ManagedAppsCard groupIds={groupsForStatistics} />
                <ProductsCard groupIds={groupsForStatistics} />
                <OperatingSystemsCard groupIds={groupsForStatistics} />
            </div>
        </div>
    );
};
